import { Link } from "react-router-dom";
import { Check, Home } from "lucide-react";
import UserAvatar from "@/components/common/UserAvatar";
import { cn } from "@/utils/common.utils";
import { NOTIFICATION_TYPE_ICONS } from "@/constant/account.constant";
import { notificationTimeAgo } from "@/utils/account.utils";
import type { AppNotification } from "@/types/notifications.types";
import { useAuth } from "@/lib/auth";
import { buildClientDealPath, buildCollabDealPath, buildDealDetailPath, SHARED_PROTECTED_ROUTES } from "@/routes/url";

interface NotificationRowProps {
  notification: AppNotification;
  /** Marks this row read when it is opened. */
  onOpen?: (notification: AppNotification) => void;
}

export default function NotificationRow({ notification, onOpen }: NotificationRowProps) {
  const { context } = useAuth();
  const Icon = (NOTIFICATION_TYPE_ICONS as Record<string, typeof Check>)[notification.type] || Check;
  const badgeTone = notification.type === "overdue" ? "bg-dash-rust text-white" : "bg-dash-hover text-dash-greenDeep";

  // Unread rows carry a tinted ground and a dot; read ones sit flat. The whole
  // row is the click target when it points at a deal.
  const body = (
    <>
      <div className="relative shrink-0">
        <UserAvatar name={notification.actor_name} src={notification.photo_url} className="size-9" fallbackClassName="text-[11px] font-bold" />
        <span className={cn("absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full ring-2 ring-white", badgeTone)}>
          <Icon className="size-2.5" />
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-dash-ink">{notification.title}</p>
        <p className="mt-0.5 truncate text-sm text-dash-muted sm:whitespace-normal">{notification.description}</p>
        {notification.record_label && (
          <div className="mt-1.5 flex items-center gap-1 text-xs text-dash-faint">
            <Home className="size-3 shrink-0" /> <span className="truncate">{notification.record_label}</span>
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {!notification.read && (
          <span className="size-1.5 rounded-full bg-dash-greenDeep" aria-label="Unread" />
        )}
        <span className="whitespace-nowrap text-[10px] text-dash-faint sm:text-xs">{notificationTimeAgo(notification.at)}</span>
      </div>
    </>
  );

  const className = cn(
    "flex w-full items-start gap-4 border-b border-dash-border px-4 py-4 text-left last:border-b-0 sm:px-6",
    !notification.read && "bg-dash-hover/40",
    notification.deal_id && "transition-colors hover:bg-dash-searchBg",
  );

  if (notification.deal_id) {
    // A participant's deal lives in their portal, not on the internal studio
    // page — which would bounce them to the overview.
    const participation = (context?.deal_participations || []).find((p) => p.deal_id === notification.deal_id);
    const base = !participation
      ? buildDealDetailPath(notification.deal_id)
      : participation.participant_role === "client"
        ? buildClientDealPath(notification.deal_id)
        : buildCollabDealPath(notification.deal_id);
    // Chat nudges open the Chats page on that deal, for agents and
    // participants alike.
    const chatTarget = `${SHARED_PROTECTED_ROUTES.CHATS}?deal=${notification.deal_id}`;
    return (
      <Link
        to={notification.type === "message" ? chatTarget : base}
        className={className}
        onClick={() => onOpen?.(notification)}
        data-testid={`notification-row-${notification.id}`}
      >
        {body}
      </Link>
    );
  }

  return (
    <div className={className} data-testid={`notification-row-${notification.id}`}>
      {body}
    </div>
  );
}
